import Button from '@rio-cloud/rio-uikit/Button';
import LoadMoreButton from '@rio-cloud/rio-uikit/LoadMoreButton';
import NotFoundState from '@rio-cloud/rio-uikit/NotFoundState';
import Spinner from '@rio-cloud/rio-uikit/Spinner';
import TableHead from '@rio-cloud/rio-uikit/TableHead';
import TableToolbar from '@rio-cloud/rio-uikit/TableToolbar';
import SortDirection, { type SortDirectionType } from '@rio-cloud/rio-uikit/utils/SortUtils';
import { PLACEMENT } from '@rio-cloud/rio-uikit/values/Placement';
import type React from 'react';
import { cloneElement, type ReactElement, useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { SortableHeaderWithTooltip } from '@/components/Table/SortableHeaderWithTooltip';
import TableFilter from '@/components/Table/TableFilter';
import { useTableSorting } from '@/components/Table/useTableSorting';
import Tooltip from '@/components/Tooltip/Tooltip';
import { t } from '@/lib/utils';

const INITIAL_PAGE_SIZE = 20;

export interface Column<T> {
    key: string;
    label: string | React.ReactElement;
    headerTooltip?: React.ReactNode;
    valueGetter: (row: T) => any;
    renderCell: (props: { row: T }) => React.ReactNode;
    sortable?: boolean;
    width?: string;
}

interface TableProps<T> {
    data: T[];
    columns: Column<T>[];
    isLoading?: boolean;
    searchByLabel: string;
    noMoreMessage?: string;
    noResults: {
        title: string;
        description: string;
    };
    nonFilterableColumns?: string[];
    isSidebarOpen?: boolean;
    openRowSidebar?: (row: T) => void;
    disableRowSelection?: boolean;
    overview?: React.ReactNode;
    showFilter?: boolean;
    extraFilters?: React.ReactNode;
    extraFiltersFn?: (row: T) => boolean;
    showTableSettings?: boolean;
    tableSettingsDialog?: (show: boolean, onHide: () => void) => ReactElement;
    initialSortKey: string;
    initialSortDirection?: SortDirectionType;
    getRowKey?: (row: T, index: number) => string | number;
}

export function Table<T>({
    data,
    columns,
    isLoading,
    searchByLabel,
    noMoreMessage,
    noResults,
    nonFilterableColumns = [],
    isSidebarOpen,
    openRowSidebar,
    disableRowSelection,
    overview,
    showFilter,
    extraFilters,
    extraFiltersFn,
    showTableSettings = false,
    tableSettingsDialog,
    initialSortKey,
    initialSortDirection = SortDirection.ASCENDING,
    getRowKey,
}: TableProps<T>) {
    const [searchValue, setSearchValue] = useState('');
    const [activeRowId, setActiveRowId] = useState<number | null>(null);
    const [pageSize, setPageSize] = useState(INITIAL_PAGE_SIZE);
    const [showTableSettingsDialog, setShowTableSettingsDialog] = useState(false);

    const tableRef = useRef<HTMLTableElement>(null);

    useEffect(() => {
        if (!isSidebarOpen) {
            setActiveRowId(null);
        }
    }, [isSidebarOpen]);

    const searchFilteredData = data.filter((row: T) => {
        if (!searchValue) {
            return true;
        }
        return columns.some(col => {
            if (nonFilterableColumns.includes(col.key)) {
                return false;
            }
            const value = String(col.valueGetter(row)).toLowerCase();
            return value.includes(searchValue.toLowerCase());
        });
    });

    const fullyFilteredData = extraFiltersFn ? searchFilteredData.filter(extraFiltersFn) : searchFilteredData;

    const { sortedData, sortKey, sortDirection, handleSortChange } = useTableSorting(
        fullyFilteredData,
        columns,
        initialSortKey as keyof T,
        initialSortDirection
    );

    const visibleData = sortedData.slice(0, pageSize);

    return (
        <>
            <TableToolbar>
                <div className='display-flex'>
                    {showFilter && (
                        <TableFilter filter={searchValue} setFilter={setSearchValue} searchByLabel={searchByLabel} />
                    )}

                    <div className='display-flex flex-1-0'>
                        {extraFilters && cloneElement(extraFilters as React.ReactElement, { rows: searchFilteredData })}
                    </div>

                    {showTableSettings && (
                        <div
                            className='display-flex align-items-end padding-left-10'
                            data-test-id='table-settings-button'
                        >
                            <Tooltip tooltipContent={t('tableSettings.title')} placement={PLACEMENT.BOTTOM_END}>
                                <Button iconOnly iconName='settings' onClick={() => setShowTableSettingsDialog(true)} />
                            </Tooltip>
                        </div>
                    )}
                </div>
                {showTableSettingsDialog &&
                    tableSettingsDialog?.(showTableSettingsDialog, () => setShowTableSettingsDialog(false))}
            </TableToolbar>

            {overview && cloneElement(overview as React.ReactElement, { rows: fullyFilteredData })}

            <div className='table-responsive' data-testid='data-table'>
                <table ref={tableRef} className='table-hover table-head-filled table-bordered table table-layout-fixed'>
                    <thead>
                        <tr>
                            {columns.map(col => {
                                if (col.sortable && col.headerTooltip) {
                                    return (
                                        <SortableHeaderWithTooltip
                                            key={col.key}
                                            col={col}
                                            sortKey={sortKey as string}
                                            sortDirection={sortDirection}
                                            onClick={handleSortChange}
                                        />
                                    );
                                }

                                return col.sortable ? (
                                    <TableHead
                                        key={col.key}
                                        column={col.key}
                                        label={col.label}
                                        sortBy={sortKey as string}
                                        sortDir={sortDirection}
                                        onClick={handleSortChange}
                                        className={col.width}
                                    />
                                ) : (
                                    <th key={col.key} className={col.width}>
                                        {col.label}
                                    </th>
                                );
                            })}
                        </tr>
                    </thead>
                    <tbody>
                        {isLoading ? (
                            <tr>
                                <td colSpan={columns.length} className='text-center'>
                                    <Spinner text={<FormattedMessage id='general.loading' />} />
                                </td>
                            </tr>
                        ) : visibleData.length ? (
                            visibleData.map((row, idx) => (
                                <tr
                                    key={getRowKey ? getRowKey(row, idx) : idx}
                                    className={`position-relative ${activeRowId === idx && 'active'} ${openRowSidebar && 'cursor-pointer'}`}
                                    onClick={() => {
                                        if (!disableRowSelection) {
                                            setActiveRowId(idx);
                                        }
                                        openRowSidebar?.(row);
                                    }}
                                >
                                    {columns.map(col => (
                                        <td key={col.key}>
                                            <div style={col.key !== 'action' ? { pointerEvents: 'none' } : undefined}>
                                                {col.renderCell({ row })}
                                            </div>
                                        </td>
                                    ))}
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={columns.length}>
                                    <NotFoundState
                                        headline={noResults.title}
                                        message={noResults.description}
                                        outerClassName='border-none'
                                    />
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {fullyFilteredData.length > 0 && (
                <LoadMoreButton
                    loaded={visibleData.length}
                    total={fullyFilteredData.length}
                    onLoadMore={() => setPageSize(p => p + INITIAL_PAGE_SIZE)}
                    loadMoreMessage={t('general.loadMore')}
                    noMoreMessage={noMoreMessage}
                    data-testid='load-more-button'
                />
            )}
        </>
    );
}
